// application/useCapacitacionForm.js  (carga por uuid + guardado orquestado). Sin JSX, sin Swal.
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { capacitacionesRepository } from '../infrastructure/capacitaciones.repository';
import { mediaRepository } from '../../shared/infrastructure/media.repository';
import { guardarConHijos, reintentarImagen } from '../../shared/application/guardarConHijos';
import { createCapacitacion } from '../domain/capacitacion';
import { toApiDate, toApiDateTime } from '../../shared/domain/fecha';
import { MSG_SIN_ASISTENTES } from '../domain/capacitacion.rules';
import { DomainError } from '../../shared/domain/DomainError';
import { asistentesFinales, asistentesVigentes, construirAsistentesPayload } from '../../shared/domain/asistentes';

const TIPO = 'capacitacion';

// uuid presente => modo edición. El uuid nuevo y la fecha de hoy se generan AQUÍ (application), no en domain.
export const useCapacitacionForm = ({ uuid, repository = capacitacionesRepository, media = mediaRepository } = {}) => {
  const isEdit = !!uuid;
  const [state, setState] = useState({ data: null, status: isEdit ? 'loading' : 'success', error: null });
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  // Asistentes vigentes [{ uuid, id_cuenta }] conocidos (cargados o devueltos por el último guardado): se reenvían sus uuids.
  const asistentesRef = useRef([]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) {
      asistentesRef.current = [];
      setState({ data: createCapacitacion({ uuid: uuidv4(), fecha: toApiDate(new Date()) }), status: 'success', error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, status: 'loading', error: null });
    repository.get(uuid).then(
      data => {
        if (!mounted.current || cancelled) return;
        asistentesRef.current = asistentesVigentes(data.asistentes);
        setState({ data, status: 'success', error: null });
      },
      error => mounted.current && !cancelled && setState({ data: null, status: 'error', error })
    );
    return () => {
      cancelled = true;
    };
  }, [uuid, isEdit, repository]);

  /**
   * Orquesta (guardarConHijos): valida (>= 1 asistente) ANTES de cualquier petición -> capacitación (con `asistentes`
   * en el mismo payload) -> eliminar imágenes marcadas -> subir imágenes pendientes.
   * `extras`: { asistenteIds, pendientes: [{uuid, file}], eliminaciones: [uuid], onImagenEstado, onEliminacionEstado }.
   * Devuelve { guardado, imagenes, eliminaciones } (un fallo de imagen no lanza); lanza DomainError si falla la capacitación.
   */
  const save = useCallback(
    async (values, { asistenteIds = [], pendientes = [], eliminaciones = [], onImagenEstado, onEliminacionEstado } = {}) => {
      const creado = toApiDateTime(new Date());
      if (asistenteIds.length === 0) throw new DomainError(MSG_SIN_ASISTENTES);
      // Quien ya era asistente conserva su uuid; solo los agregados reciben uno nuevo.
      const payloadAsistentes = construirAsistentesPayload(asistenteIds, asistentesRef.current, uuidv4);

      setSaving(true);
      try {
        return await guardarConHijos({
          tipo: TIPO,
          entidad: 'la capacitación',
          guardarPadre: async () => {
            const entity = createCapacitacion({ ...state.data, ...values });
            const guardada = await (isEdit
              ? repository.update(entity, { asistentes: payloadAsistentes })
              : repository.create(entity, { asistentes: payloadAsistentes }));
            asistentesRef.current = asistentesFinales(guardada, payloadAsistentes);
            return guardada;
          },
          media,
          pendientes,
          eliminaciones,
          creado,
          onImagenEstado,
          onEliminacionEstado,
        });
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [state.data, isEdit, repository, media]
  );

  /** Reintenta solo UNA imagen contra la entidad ya guardada (usa su `id` numérico); no vuelve a guardar el formulario. */
  const retryImagen = useCallback(
    (item, onImagenEstado) => {
      if (!state.data || !state.data.id) return Promise.resolve(null);
      return reintentarImagen({ tipo: TIPO, idPadre: state.data.id, item, media, creado: toApiDateTime(new Date()), onImagenEstado });
    },
    [state.data, media]
  );

  return {
    data: state.data,
    status: state.status,
    error: state.error,
    isEdit,
    saving,
    save,
    reintentarImagen: retryImagen,
  };
};

export default useCapacitacionForm;
